/* Copyright (c) 2023 Xperi. All Rights Reserved. */

import { Operations, Payload } from '@tivocorp/tivo-webapp-loggers';
import { PostSignInError, PreSignInError, SignInError } from '../error';
import { Events, EventProperties, SignInStates } from './AppStartOperationsConstants';

export class AppStartOperationsLogger {
    private static addErrorToPayload = (
        payload: Payload,
        error: PreSignInError | SignInError | PostSignInError,
    ) => {
        payload.add(EventProperties.ERROR_CODE, error.getCode());
        payload.add(EventProperties.ERROR_TYPE, error.getType());
        payload.add(EventProperties.ERROR_MESSAGE, error.getMessage());
    };

    private static addReferenceTime = (payload: Payload) => {
        payload.add(EventProperties.REFERENCE_TIME, new Date().getTime());
    };

    static logAccessTokenGet = () => {
        const payload = new Payload();
        payload.add(EventProperties.SIGN_IN_STATE, SignInStates.ACCESS_TOKEN_GET);
        payload.add(EventProperties.EVENT_NAME, Events.ACCESS_TOKEN_GET);
        AppStartOperationsLogger.addReferenceTime(payload);
        Operations.log(payload);
    };

    static logAccessTokenSuccessful = () => {
        const payload = new Payload();
        payload.add(EventProperties.SIGN_IN_STATE, SignInStates.ACCESS_TOKEN_GET);
        payload.add(EventProperties.EVENT_NAME, Events.ACCESS_TOKEN_SUCCESSFUL);
        AppStartOperationsLogger.addReferenceTime(payload);
        Operations.log(payload);
    };

    static logAccessTokenFailed = (error: SignInError) => {
        const payload = new Payload();
        payload.add(EventProperties.SIGN_IN_STATE, SignInStates.ACCESS_TOKEN_GET);
        payload.add(EventProperties.EVENT_NAME, Events.ACCESS_TOKEN_FAILED);
        AppStartOperationsLogger.addErrorToPayload(payload, error);
        AppStartOperationsLogger.addReferenceTime(payload);
        Operations.log(payload);
    };

    static logAccessTokenRestoration = () => {
        const payload = new Payload();
        payload.add(EventProperties.SIGN_IN_STATE, SignInStates.ACCESS_TOKEN_GET);
        payload.add(EventProperties.EVENT_NAME, Events.ACCESS_TOKEN_RESTORATION);
        AppStartOperationsLogger.addReferenceTime(payload);
        Operations.log(payload);
    };

    static logAuthenticationConfigurationGet = () => {
        const payload = new Payload();
        payload.add(EventProperties.SIGN_IN_STATE, SignInStates.AUTH_CONFIGURATIONS_GET);
        payload.add(EventProperties.EVENT_NAME, Events.AUTHENTICATION_CONFIGURATION_GET);
        AppStartOperationsLogger.addReferenceTime(payload);
        Operations.log(payload);
    };

    static logAuthenticationConfigurationSuccessful = (authType, domain, licensePlateUrl) => {
        const payload = new Payload();
        payload.add(EventProperties.SIGN_IN_STATE, SignInStates.AUTH_CONFIGURATIONS_GET);
        payload.add(EventProperties.EVENT_NAME, Events.AUTHENTICATION_CONFIGURATION_SUCCESSFUL);
        payload.add(EventProperties.AUTHENTICATION_TYPE, authType);
        payload.add(EventProperties.DOMAIN, domain);
        payload.add(EventProperties.LICENSE_PLATE_URL, licensePlateUrl || '');
        AppStartOperationsLogger.addReferenceTime(payload);
        Operations.log(payload);
    };

    static logAuthenticationConfigurationFailed = (error: SignInError) => {
        const payload = new Payload();
        payload.add(EventProperties.SIGN_IN_STATE, SignInStates.AUTH_CONFIGURATIONS_GET);
        payload.add(EventProperties.EVENT_NAME, Events.AUTHENTICATION_CONFIGURATION_FAILED);
        AppStartOperationsLogger.addErrorToPayload(payload, error);
        AppStartOperationsLogger.addReferenceTime(payload);
        Operations.log(payload);
    };

    static logAuthenticationConfigurationRestoration = () => {
        const payload = new Payload();
        payload.add(EventProperties.SIGN_IN_STATE, SignInStates.AUTH_CONFIGURATIONS_GET);
        payload.add(EventProperties.EVENT_NAME, Events.AUTHENTICATION_CONFIGURATION_RESTORATION);
        AppStartOperationsLogger.addReferenceTime(payload);
        Operations.log(payload);
    };

    static logBodyInfoGet = () => {
        const payload = new Payload();
        payload.add(EventProperties.SIGN_IN_STATE, SignInStates.BODY_INFO_GET);
        payload.add(EventProperties.EVENT_NAME, Events.BODY_INFO_GET);
        AppStartOperationsLogger.addReferenceTime(payload);
        Operations.log(payload);
    };

    static logBodyInfoSuccessful = () => {
        const payload = new Payload();
        payload.add(EventProperties.SIGN_IN_STATE, SignInStates.BODY_INFO_GET);
        payload.add(EventProperties.EVENT_NAME, Events.BODY_INFO_SUCCESSFUL);
        AppStartOperationsLogger.addReferenceTime(payload);
        Operations.log(payload);
    };

    static logBodyInfoFailed = (error: PostSignInError) => {
        const payload = new Payload();
        payload.add(EventProperties.SIGN_IN_STATE, SignInStates.BODY_INFO_GET);
        payload.add(EventProperties.EVENT_NAME, Events.BODY_INFO_FAILED);
        AppStartOperationsLogger.addErrorToPayload(payload, error);
        AppStartOperationsLogger.addReferenceTime(payload);
        Operations.log(payload);
    };

    static logBodyInfoRestoration = () => {
        const payload = new Payload();
        payload.add(EventProperties.SIGN_IN_STATE, SignInStates.BODY_INFO_GET);
        payload.add(EventProperties.EVENT_NAME, Events.BODY_INFO_RESTORATION);
        AppStartOperationsLogger.addReferenceTime(payload);
        Operations.log(payload);
    };

    static logCancelSignin = (reason) => {
        const payload = new Payload();
        payload.add(EventProperties.SIGN_IN_STATE, SignInStates.LICENSE_PLATE_GET);
        payload.add(EventProperties.EVENT_NAME, Events.LICENSE_PLATE_RESTORATION);
        payload.add(EventProperties.REASON, reason);
        AppStartOperationsLogger.addReferenceTime(payload);
        Operations.log(payload);
    };

    static logDeviceFeatureGet = () => {
        const payload = new Payload();
        payload.add(EventProperties.SIGN_IN_STATE, SignInStates.DEVICE_FEATURES_GET);
        payload.add(EventProperties.EVENT_NAME, Events.DEVICE_FEATURE_GET);
        AppStartOperationsLogger.addReferenceTime(payload);
        Operations.log(payload);
    };

    static logDeviceFeatureSuccessful = () => {
        const payload = new Payload();
        payload.add(EventProperties.SIGN_IN_STATE, SignInStates.DEVICE_FEATURES_GET);
        payload.add(EventProperties.EVENT_NAME, Events.DEVICE_FEATURE_SUCCESSFUL);
        AppStartOperationsLogger.addReferenceTime(payload);
        Operations.log(payload);
    };

    static logDeviceFeatureFailed = (error: PostSignInError) => {
        const payload = new Payload();
        payload.add(EventProperties.SIGN_IN_STATE, SignInStates.DEVICE_FEATURES_GET);
        payload.add(EventProperties.EVENT_NAME, Events.DEVICE_FEATURE_GET);
        AppStartOperationsLogger.addErrorToPayload(payload, error);
        AppStartOperationsLogger.addReferenceTime(payload);
        Operations.log(payload);
    };

    static logDeviceFeatureRestoration = () => {
        const payload = new Payload();
        payload.add(EventProperties.SIGN_IN_STATE, SignInStates.DEVICE_FEATURES_GET);
        payload.add(EventProperties.EVENT_NAME, Events.DEVICE_FEATURE_RESTORATION);
        AppStartOperationsLogger.addReferenceTime(payload);
        Operations.log(payload);
    };

    static logLicensePlateGet = () => {
        const payload = new Payload();
        payload.add(EventProperties.SIGN_IN_STATE, SignInStates.LICENSE_PLATE_GET);
        payload.add(EventProperties.EVENT_NAME, Events.LICENSE_PLATE_GET);
        AppStartOperationsLogger.addReferenceTime(payload);
        Operations.log(payload);
    };

    static logLicensePlateSuccessful = () => {
        const payload = new Payload();
        payload.add(EventProperties.SIGN_IN_STATE, SignInStates.LICENSE_PLATE_GET);
        payload.add(EventProperties.EVENT_NAME, Events.LICENSE_PLATE_SUCCESSFUL);
        AppStartOperationsLogger.addReferenceTime(payload);
        Operations.log(payload);
    };

    static logLicensePlateFailed = (error: SignInError) => {
        const payload = new Payload();
        payload.add(EventProperties.SIGN_IN_STATE, SignInStates.LICENSE_PLATE_GET);
        payload.add(EventProperties.EVENT_NAME, Events.LICENSE_PLATE_FAILED);
        AppStartOperationsLogger.addErrorToPayload(payload, error);
        AppStartOperationsLogger.addReferenceTime(payload);
        Operations.log(payload);
    };

    static logLicensePlateRestoration = () => {
        const payload = new Payload();
        payload.add(EventProperties.SIGN_IN_STATE, SignInStates.LICENSE_PLATE_GET);
        payload.add(EventProperties.EVENT_NAME, Events.LICENSE_PLATE_RESTORATION);
        AppStartOperationsLogger.addReferenceTime(payload);
        Operations.log(payload);
    };

    static logMsoDefaultBrandingUiBundleGet = () => {
        const payload = new Payload();
        payload.add(EventProperties.SIGN_IN_STATE, SignInStates.MSO_DEFAULT_BRANDING_UI_BUNDLE_GET);
        payload.add(EventProperties.EVENT_NAME, Events.MSO_DEFAULT_BRANDING_UI_BUNDLE_GET);
        AppStartOperationsLogger.addReferenceTime(payload);
        Operations.log(payload);
    };

    static logMsoDefaultBrandingUiBundleSuccessful = () => {
        const payload = new Payload();
        payload.add(EventProperties.SIGN_IN_STATE, SignInStates.MSO_DEFAULT_BRANDING_UI_BUNDLE_GET);
        payload.add(EventProperties.EVENT_NAME, Events.MSO_DEFAULT_BRANDING_UI_BUNDLE_SUCCESSFUL);
        AppStartOperationsLogger.addReferenceTime(payload);
        Operations.log(payload);
    };

    static logMsoDefaultBrandingUiBundleFailed = (error: PreSignInError) => {
        const payload = new Payload();
        payload.add(EventProperties.SIGN_IN_STATE, SignInStates.MSO_DEFAULT_BRANDING_UI_BUNDLE_GET);
        payload.add(EventProperties.EVENT_NAME, Events.MSO_DEFAULT_BRANDING_UI_BUNDLE_FAILED);
        AppStartOperationsLogger.addErrorToPayload(payload, error);
        AppStartOperationsLogger.addReferenceTime(payload);
        Operations.log(payload);
    };

    static logMsoServiceIdNotFound = (error: PostSignInError) => {
        const payload = new Payload();
        payload.add(EventProperties.SIGN_IN_STATE, SignInStates.DEVICE_FEATURES_GET);
        payload.add(EventProperties.EVENT_NAME, Events.DEVICE_FEATURE_GET);
        AppStartOperationsLogger.addErrorToPayload(payload, error);
        AppStartOperationsLogger.addReferenceTime(payload);
        Operations.log(payload);
    };

    static logProvisioningDataGet = () => {
        const payload = new Payload();
        payload.add(EventProperties.SIGN_IN_STATE, SignInStates.PROVISIONING_DATA_GET);
        payload.add(EventProperties.EVENT_NAME, Events.PROVISIONING_DATA_GET);
        AppStartOperationsLogger.addReferenceTime(payload);
        Operations.log(payload);
    };

    static logProvisioningDataSuccessful = () => {
        const payload = new Payload();
        payload.add(EventProperties.SIGN_IN_STATE, SignInStates.PROVISIONING_DATA_GET);
        payload.add(EventProperties.EVENT_NAME, Events.PROVISIONING_DATA_SUCCESSFUL);
        AppStartOperationsLogger.addReferenceTime(payload);
        Operations.log(payload);
    };

    static logProvisioningDataFailed = (error: any) => {
        const payload = new Payload();
        payload.add(EventProperties.SIGN_IN_STATE, SignInStates.PROVISIONING_DATA_GET);
        payload.add(EventProperties.EVENT_NAME, Events.PROVISIONING_DATA_FAILED);
        AppStartOperationsLogger.addErrorToPayload(payload, error?.payload);
        AppStartOperationsLogger.addReferenceTime(payload);
        Operations.log(payload);
    };

    static logProvisioningDataRestoration = () => {
        const payload = new Payload();
        payload.add(EventProperties.SIGN_IN_STATE, SignInStates.PROVISIONING_DATA_GET);
        payload.add(EventProperties.EVENT_NAME, Events.PROVISIONING_DATA_RESTORATION);
        AppStartOperationsLogger.addReferenceTime(payload);
        Operations.log(payload);
    };

    static logSamlTokenGet = () => {
        const payload = new Payload();
        payload.add(EventProperties.SIGN_IN_STATE, SignInStates.SAML_TOKEN_GET);
        payload.add(EventProperties.EVENT_NAME, Events.SAML_TOKEN_GET);
        AppStartOperationsLogger.addReferenceTime(payload);
        Operations.log(payload);
    };

    static logSamlTokenSuccessful = () => {
        const payload = new Payload();
        payload.add(EventProperties.SIGN_IN_STATE, SignInStates.SAML_TOKEN_GET);
        payload.add(EventProperties.EVENT_NAME, Events.SAML_TOKEN_SUCCESSFUL);
        AppStartOperationsLogger.addReferenceTime(payload);
        Operations.log(payload);
    };

    static logSamlTokenFailed = (error: SignInError) => {
        const payload = new Payload();
        payload.add(EventProperties.SIGN_IN_STATE, SignInStates.SAML_TOKEN_GET);
        payload.add(EventProperties.EVENT_NAME, Events.SAML_TOKEN_FAILED);
        AppStartOperationsLogger.addErrorToPayload(payload, error);
        AppStartOperationsLogger.addReferenceTime(payload);
        Operations.log(payload);
    };

    static logSamlTokenRestoration = () => {
        const payload = new Payload();
        payload.add(EventProperties.SIGN_IN_STATE, SignInStates.SAML_TOKEN_GET);
        payload.add(EventProperties.EVENT_NAME, Events.SAML_TOKEN_RESTORATION);
        AppStartOperationsLogger.addReferenceTime(payload);
        Operations.log(payload);
    };

    static logSlsGlobalEndpointsSuccessful = () => {
        const payload = new Payload();
        payload.add(EventProperties.SIGN_IN_STATE, SignInStates.SLS_GLOBAL_ENDPOINT_GET);
        payload.add(EventProperties.EVENT_NAME, Events.SLS_GLOBAL_ENDPOINTS_SUCCESSFUL);
        AppStartOperationsLogger.addReferenceTime(payload);
        Operations.log(payload);
    };

    static logSlsGlobalEndpointsFailed = (error: PreSignInError) => {
        const payload = new Payload();
        payload.add(EventProperties.SIGN_IN_STATE, SignInStates.SLS_GLOBAL_ENDPOINT_GET);
        payload.add(EventProperties.EVENT_NAME, Events.SLS_GLOBAL_ENDPOINTS_FAILED);
        AppStartOperationsLogger.addErrorToPayload(payload, error);
        AppStartOperationsLogger.addReferenceTime(payload);
        Operations.log(payload);
    };

    static logSlsGlobalEndpointsRestoration = () => {
        const payload = new Payload();
        payload.add(EventProperties.SIGN_IN_STATE, SignInStates.SLS_GLOBAL_ENDPOINT_GET);
        payload.add(EventProperties.EVENT_NAME, Events.SLS_GLOBAL_ENDPOINTS_RESTORATION);
        AppStartOperationsLogger.addReferenceTime(payload);
        Operations.log(payload);
    };

    static logStartSlsGlobalEndpointsGet = () => {
        const payload = new Payload();
        payload.add(EventProperties.SIGN_IN_STATE, SignInStates.SLS_GLOBAL_ENDPOINT_GET);
        payload.add(EventProperties.EVENT_NAME, Events.SLS_GLOBAL_ENDPOINTS_GET);
        AppStartOperationsLogger.addReferenceTime(payload);
        Operations.log(payload);
    };

    static logSlsServiceEndpointsGet = () => {
        const payload = new Payload();
        payload.add(EventProperties.SIGN_IN_STATE, SignInStates.SLS_SERVICE_ENDPOINTS_GET);
        payload.add(EventProperties.EVENT_NAME, Events.SLS_SERVICE_ENDPOINTS_GET);
        AppStartOperationsLogger.addReferenceTime(payload);
        Operations.log(payload);
    };

    static logSlsServiceEndpointsSuccessful = () => {
        const payload = new Payload();
        payload.add(EventProperties.SIGN_IN_STATE, SignInStates.SLS_SERVICE_ENDPOINTS_GET);
        payload.add(EventProperties.EVENT_NAME, Events.SLS_SERVICE_ENDPOINTS_SUCCESSFUL);
        AppStartOperationsLogger.addReferenceTime(payload);
        Operations.log(payload);
    };

    static logSlsServiceEndpointsFailed = (error: PostSignInError) => {
        const payload = new Payload();
        payload.add(EventProperties.SIGN_IN_STATE, SignInStates.SLS_SERVICE_ENDPOINTS_GET);
        payload.add(EventProperties.EVENT_NAME, Events.SLS_SERVICE_ENDPOINTS_FAILED);
        AppStartOperationsLogger.addErrorToPayload(payload, error);
        AppStartOperationsLogger.addReferenceTime(payload);
        Operations.log(payload);
    };

    static logSlsServiceEndpointsRestoration = () => {
        const payload = new Payload();
        payload.add(EventProperties.SIGN_IN_STATE, SignInStates.SLS_SERVICE_ENDPOINTS_GET);
        payload.add(EventProperties.EVENT_NAME, Events.SLS_SERVICE_ENDPOINTS_RESTORATION);
        AppStartOperationsLogger.addReferenceTime(payload);
        Operations.log(payload);
    };

    static logUIErrorCode = (error: string) => {
        const payload = new Payload();
        payload.add(EventProperties.EVENT_NAME, 'UIErrorCode');
        payload.add('CODE', error);
        AppStartOperationsLogger.addReferenceTime(payload);
        Operations.log(payload);
    };
}
